import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { productsApi } from "../../api/products";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import { getProductImageUrls } from "../../utils/productMedia";
import {
  formatImageSize,
  optimizeProductImages,
} from "../../utils/productImageUpload";
import {
  PRODUCT_IMAGE_MAX_SOURCE_BYTES,
  describeImageUploadError,
  uploadProgressPercent,
} from "../../utils/imageUpload";
import ItemOperationalFields, { capabilitiesForKind } from "../../components/dashboard/items/ItemOperationalFields";
import ProductVariantsEditor from "../../components/dashboard/items/ProductVariantsEditor";
import QuantityDiscountsEditor, {
  normalizeQuantityDiscountRows,
  quantityDiscountEligibility,
  validateQuantityDiscountRows,
} from "../../components/dashboard/items/QuantityDiscountsEditor";
import { hasProjectPermission, normalizeProjectAccess } from "../../utils/projectAccess";
import {
  isTrialProject,
  TRIAL_ITEMS_LIMIT,
  TRIAL_PRODUCTS_EVENT,
} from "../../utils/trialOnboarding";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const MAX_IMAGES = 8;

const emptyForm = {
  itemType: "product",
  erpItemKind: "product",
  name: "",
  description: "",
  category: "General",
  code: "",
  unit: "",
  priceType: "fixed",
  price: "0",
  quantityPriceTiers: [],
  stock: "0",
  durationMinutes: "",
  modality: "",
  requiresBooking: false,
  requiresShipping: true,
  bookingConfirmationMode: "manual",
  bookingPaymentMode: "none",
  bookingDepositType: "fixed",
  bookingDepositValue: "0",
  capacity: "1",
  bufferBeforeMinutes: "0",
  bufferAfterMinutes: "0",
  serviceLocation: "",
  meetingUrl: "",
  bookingInstructions: "",
  cancellationWindowHours: "0",
  tags: "",
  attributes: [],
  hasVariants: false,
  variantAttributes: [],
  variants: [],
  active: true,
  ...capabilitiesForKind("product"),
  lastCost: "0",
  averageCost: "0",
  standardCost: "0",
  costingMethod: "last_cost",
  costCurrency: "ARS",
  allowNegativeStock: false,
  minimumStock: "0",
  reorderPoint: "0",
  inventoryLocation: "",
  salesAccountCode: "SALES",
  purchaseAccountCode: "PURCHASES",
  inventoryAccountCode: "INVENTORY",
  costAccountCode: "COST_OF_GOODS",
  expenseAccountCode: "OTHER_EXPENSE",
  taxProfileCode: "",
  taxRate: "0",
  taxIncludedInPrice: false,
};

function normalizeItemType(product = {}) {
  const value = String(
    product.itemType || product.type || product.tipo || "product",
  )
    .trim()
    .toLowerCase();
  return ["service", "servicio"].includes(value) ? "service" : "product";
}

const ITEM_KIND_LABELS = {
  product: "Producto para vender",
  service: "Servicio para vender",
  supply: "Insumo o consumible",
  expense: "Gasto habitual",
  asset: "Activo o equipamiento",
  charge: "Cargo o concepto",
};

function normalizeItemKind(product = {}) {
  const value = String(
    product.erpItemKind || product.itemKind || product.itemClass || normalizeItemType(product),
  )
    .trim()
    .toLowerCase();
  return ITEM_KIND_LABELS[value] ? value : normalizeItemType(product);
}

function productCapabilities(product = {}) {
  const itemKind = normalizeItemKind(product);
  const defaults = capabilitiesForKind(itemKind);
  const supplied = {
    ...(product.capabilities || {}),
    ...(product.operational || {}),
  };

  return {
    ...defaults,
    ...supplied,
    reservable: Boolean(
      supplied.reservable ??
        product.requiresBooking ??
        product.requiereReserva ??
        defaults.reservable,
    ),
    shippable: Boolean(
      supplied.shippable ??
        product.requiresShipping ??
        product.requiereEnvio ??
        defaults.shippable,
    ),
  };
}

function supportsInventoryForKind(kind) {
  return kind === "product" || kind === "supply";
}

function supportsVariantsForKind(kind) {
  return supportsInventoryForKind(kind);
}

function isReservableService(form = {}) {
  return (
    form.erpItemKind === "service" &&
    Boolean(form.reservable || form.requiresBooking)
  );
}

function normalizePriceType(product = {}) {
  const value = String(product.priceType || product.precioTipo || "fixed")
    .trim()
    .toLowerCase();
  if (["from", "desde"].includes(value)) return "from";
  if (["quote", "consultar"].includes(value)) return "quote";
  return "fixed";
}

function attributesToRows(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value)
    .map(([label, detail]) => ({
      label: String(label),
      value: String(detail ?? ""),
    }))
    .filter((row) => row.label || row.value);
}

function rowsToAttributes(rows = []) {
  return rows.reduce((result, row) => {
    const label = String(row?.label || "").trim();
    const value = String(row?.value || "").trim();
    if (label && value) result[label] = value;
    return result;
  }, {});
}

function tagsToText(product = {}) {
  const tags = product.tags || product.etiquetas || [];
  return (Array.isArray(tags) ? tags : [])
    .map((tag) => (typeof tag === "string" ? tag : tag?.title))
    .filter(Boolean)
    .join(", ");
}

function variantAttributesToForm(product = {}) {
  const source = product.variantAttributes || product.variantOptions || [];
  return (Array.isArray(source) ? source : []).map((axis, index) => ({
    key: String(axis?.key || axis?.id || `opcion_${index + 1}`),
    label: String(axis?.label || axis?.name || `Opción ${index + 1}`),
    values: Array.isArray(axis?.values || axis?.options)
      ? (axis.values || axis.options).map(String).filter(Boolean)
      : [],
    displayType: axis?.displayType || axis?.type || "button",
  }));
}

function variantsToForm(product = {}) {
  const source = product.variants || product.variations || [];
  return (Array.isArray(source) ? source : []).map((variant) => ({
    id: String(variant?.id || variant?._id || ""),
    attributes: variant?.attributes || variant?.combination || {},
    sku: variant?.sku || variant?.codigo || "",
    barcode: variant?.barcode || "",
    price:
      variant?.price === null || variant?.price === undefined
        ? ""
        : String(variant.price),
    stock: String(variant?.stock ?? 0),
    imageRef: variant?.imagePath || "",
    active: variant?.active !== false,
  }));
}

function quantityPriceTiersToForm(product = {}) {
  const source =
    product.quantityPriceTiers ||
    product.quantityDiscounts ||
    product.descuentosPorCantidad ||
    [];
  return (Array.isArray(source) ? source : []).map((tier, index) => ({
    key: `quantity-tier-${productId(product) || "item"}-${index}`,
    minQuantity: String(tier?.minQuantity ?? tier?.quantity ?? ""),
    unitPrice: String(tier?.unitPrice ?? tier?.price ?? ""),
  }));
}

function validateVariantForm(form = {}, { stockManagedBySystem = false } = {}) {
  if (!form.hasVariants || !supportsVariantsForKind(form.erpItemKind)) return "";
  const axes = Array.isArray(form.variantAttributes) ? form.variantAttributes : [];
  const variants = Array.isArray(form.variants) ? form.variants : [];
  if (!axes.length) return "Agregá al menos una característica, por ejemplo Color o Talle.";
  for (const axis of axes) {
    if (!String(axis.label || "").trim()) return "Completá el nombre de cada característica.";
    if (!Array.isArray(axis.values) || !axis.values.length) {
      return `Agregá opciones para ${axis.label || "cada característica"}.`;
    }
  }
  if (!variants.length) return "Generá las combinaciones antes de guardar el producto.";
  if (variants.length > 250) return "El producto no puede superar 250 variantes.";

  const skuSet = new Set();
  const combinationSet = new Set();
  for (const variant of variants) {
    const combinationParts = [];
    for (const axis of axes) {
      const value = String(variant.attributes?.[axis.key] || "").trim();
      if (!value) return `Falta ${axis.label || "una opción"} en una variante.`;
      combinationParts.push(`${axis.key}:${value.toLocaleLowerCase("es")}`);
    }
    const combination = combinationParts.join("|");
    if (combinationSet.has(combination)) return "Hay combinaciones de variantes repetidas.";
    combinationSet.add(combination);

    const sku = String(variant.sku || "").trim().toLocaleLowerCase("es");
    if (sku && skuSet.has(sku)) return `El SKU ${variant.sku} está repetido.`;
    if (sku) skuSet.add(sku);
    if (
      form.stockTracked &&
      !stockManagedBySystem &&
      !form.allowNegativeStock &&
      Number(variant.stock || 0) < 0
    ) {
      return "El stock de una variante no puede ser negativo.";
    }
  }
  return "";
}

function variantImageOptions(existingImages = [], previewUrls = []) {
  return [
    ...existingImages.map((image) => ({ ref: image.identifier, url: image.url })),
    ...previewUrls.map((url) => ({ ref: url, url })),
  ];
}

function normalizeProductPayload(
  form,
  imageOptions = [],
  { stockManagedBySystem = false, creating = false } = {},
) {
  const isService = form.erpItemKind === "service";
  const reservable = isReservableService(form);
  const shippable =
    form.erpItemKind === "product" &&
    Boolean(form.shippable ?? form.requiresShipping);
  const tracksStock =
    supportsInventoryForKind(form.erpItemKind) && Boolean(form.stockTracked);
  const hasVariants =
    supportsVariantsForKind(form.erpItemKind) && Boolean(form.hasVariants);
  const variants = hasVariants
    ? (Array.isArray(form.variants) ? form.variants : []).map((variant) => ({
        ...(variant.id ? { id: variant.id } : {}),
        attributes: variant.attributes || {},
        sku: String(variant.sku || "").trim(),
        barcode: String(variant.barcode || "").trim(),
        price:
          variant.price === "" || variant.price === null || variant.price === undefined
            ? null
            : Number(variant.price),
        stock: tracksStock
          ? stockManagedBySystem && creating
            ? 0
            : Number(variant.stock || 0)
          : 0,
        imageIndex: imageOptions.findIndex((option) => option.ref === variant.imageRef),
        active: variant.active !== false,
      }))
    : [];
  const aggregateStock = variants.reduce(
    (sum, variant) => (variant.active === false ? sum : sum + Number(variant.stock || 0)),
    0,
  );
  const quantityPricingAllowed = quantityDiscountEligibility(form).allowed;
  return {
    itemType: isService ? "service" : "product",
    erpItemKind: form.erpItemKind,
    itemKind: form.erpItemKind,
    name: form.name,
    description: form.description,
    category: form.category || "General",
    code: form.code,
    unit: form.unit,
    priceType: form.priceType,
    price: Number(form.price || 0),
    quantityPriceTiers: quantityPricingAllowed
      ? normalizeQuantityDiscountRows(form.quantityPriceTiers)
      : [],
    stock: tracksStock
      ? stockManagedBySystem && creating
        ? 0
        : hasVariants
          ? aggregateStock
          : Number(form.stock || 0)
      : 0,
    hasVariants,
    variantAttributes: hasVariants
      ? (form.variantAttributes || []).map((axis) => ({
          key: axis.key,
          label: axis.label,
          values: Array.isArray(axis.values) ? axis.values : [],
          displayType: axis.displayType || "button",
        }))
      : [],
    variants,
    operational: {
      sellable: Boolean(form.sellable),
      purchasable: Boolean(form.purchasable),
      stockTracked: tracksStock,
      reservable,
      shippable,
      publicVisible: Boolean(form.publicVisible),
      expenseEnabled: Boolean(form.expenseEnabled),
      internalConsumptionEnabled:
        tracksStock && Boolean(form.internalConsumptionEnabled),
    },
    costing: {
      lastCost: Number(form.lastCost || 0),
      averageCost: Number(form.averageCost || 0),
      standardCost: Number(form.standardCost || 0),
      costingMethod: form.costingMethod || "last_cost",
      currency: form.costCurrency || "ARS",
    },
    inventory: {
      allowNegativeStock: tracksStock && Boolean(form.allowNegativeStock),
      minimumStock: tracksStock ? Number(form.minimumStock || 0) : 0,
      reorderPoint: tracksStock ? Number(form.reorderPoint || 0) : 0,
      location: tracksStock ? form.inventoryLocation || "" : "",
    },
    accounting: {
      salesAccountCode: form.salesAccountCode || "SALES",
      purchaseAccountCode: form.purchaseAccountCode || "PURCHASES",
      inventoryAccountCode: form.inventoryAccountCode || "INVENTORY",
      costAccountCode: form.costAccountCode || "COST_OF_GOODS",
      expenseAccountCode: form.expenseAccountCode || "OTHER_EXPENSE",
    },
    tax: {
      profileCode: form.taxProfileCode || "",
      rate: Number(form.taxRate || 0),
      includedInPrice: Boolean(form.taxIncludedInPrice),
    },
    durationMinutes: isService ? Number(form.durationMinutes || 0) : 0,
    modality: isService ? form.modality : "",
    requiresBooking: reservable,
    requiresShipping: shippable,
    bookingConfirmationMode: reservable ? form.bookingConfirmationMode : "manual",
    bookingPaymentMode: reservable ? form.bookingPaymentMode : "none",
    bookingDepositType: reservable ? form.bookingDepositType : "fixed",
    bookingDepositValue: reservable ? Number(form.bookingDepositValue || 0) : 0,
    capacity: reservable ? Math.max(1, Number(form.capacity || 1)) : 1,
    bufferBeforeMinutes: reservable ? Number(form.bufferBeforeMinutes || 0) : 0,
    bufferAfterMinutes: reservable ? Number(form.bufferAfterMinutes || 0) : 0,
    serviceLocation: isService ? form.serviceLocation : "",
    meetingUrl: reservable ? form.meetingUrl : "",
    bookingInstructions: reservable ? form.bookingInstructions : "",
    cancellationWindowHours: reservable ? Number(form.cancellationWindowHours || 0) : 0,
    tags: String(form.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    attributes: rowsToAttributes(form.attributes),
    active: Boolean(form.active),
  };
}

function priceLabel(product = {}) {
  const price = Number(product.price ?? product.precio ?? 0);
  const type = normalizePriceType(product);
  if (type === "quote") return "A consultar";
  const formatted = `$${price.toLocaleString("es-AR")}`;
  return type === "from" ? `Desde ${formatted}` : formatted;
}

function durationLabel(minutes) {
  const value = Number(minutes || 0);
  if (!value) return "Duración no indicada";
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function productId(product) {
  const source = product || {};
  return (
    source.id ||
    source._id ||
    source.code ||
    source.codigo ||
    source.name ||
    source.nombre ||
    ""
  );
}

function productName(product = {}) {
  return product.name || product.nombre || "Sin nombre";
}

function productDescription(product = {}) {
  return product.description || product.descripcion || "";
}

function productCategory(product = {}) {
  return product.category || product.categoria || "General";
}

function productCode(product = {}) {
  return product.code || product.codigo || "";
}

function productIsActive(product = {}) {
  return Boolean(product.active ?? product.activo ?? true);
}

function productStock(product = {}) {
  return Number(product.stock ?? 0);
}

function normalizeSearchValue(value) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("es");
}

function ItemFormHeading({ step, title, description }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="flex-start">
      <Box
        sx={{
          width: 32,
          height: 32,
          flex: "0 0 auto",
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND.dark,
          color: "#fff",
          fontWeight: 950,
          fontSize: 13,
          boxShadow: "0 7px 18px rgba(25,36,47,.18)",
        }}
      >
        {step}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.60)", mt: 0.35, lineHeight: 1.45 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function ProjectProductsPage() {
  const { projectId } = useParams();
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, activeProject, setActiveProject, loadProjects } =
    useProjects();
  const [products, setProducts] = React.useState([]);
  const [trialCapacity, setTrialCapacity] = React.useState(null);
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [form, setForm] = React.useState(emptyForm);
  const [editingProduct, setEditingProduct] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [upgradeOpen, setUpgradeOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [productsLoaded, setProductsLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [processingImages, setProcessingImages] = React.useState(false);
  const [imagePreparationProgress, setImagePreparationProgress] = React.useState(null);
  const [imageUploadProgress, setImageUploadProgress] = React.useState(null);
  const [error, setError] = React.useState("");
  const [imageError, setImageError] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [previewUrls, setPreviewUrls] = React.useState([]);
  const previewUrlsRef = React.useRef([]);
  const autoCreateHandledRef = React.useRef("");
  const [existingImages, setExistingImages] = React.useState([]);
  const [removedImageIds, setRemovedImageIds] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const project = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const productsEnabled = Boolean(
    project && isProjectModuleEnabled(project, "products"),
  );
  const systemEnabled = Boolean(
    project && isProjectModuleEnabled(project, "system"),
  );
  const isTrial = isTrialProject(project);
  const trialItemsLimit = Number(trialCapacity?.itemsLimit || TRIAL_ITEMS_LIMIT);
  const trialItemsUsed = Number(trialCapacity?.itemsUsed ?? products.length);
  const trialItemsRemaining = Math.max(0, trialItemsLimit - trialItemsUsed);
  const trialLimitReached = isTrial && (trialCapacity?.limitReached === true || trialItemsUsed >= trialItemsLimit);
  const trialNeedsFirstPublication = isTrial && productsLoaded && products.length === 0;
  const billingPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;
  const landingPath = `/dashboard/proyectos/${projectId}/pagina`;

  const canManageCosts =
    hasProjectPermission(memberAccess, "items.costs.manage") ||
    hasProjectPermission(memberAccess, "financial.costs.manage");
  const canManageAccounting = hasProjectPermission(
    memberAccess,
    "items.accounting.manage",
  );
  const canCreateItems = hasProjectPermission(memberAccess, "items.create");
  const canUpdateItems = hasProjectPermission(memberAccess, "items.update");
  const canDeleteItems = hasProjectPermission(memberAccess, "items.delete");
  const canRegisterPurchase = hasProjectPermission(
    memberAccess,
    "erp.purchases.create",
  );
  const canAdjustInventory = hasProjectPermission(
    memberAccess,
    "erp.stock.manage",
  );

  const categories = React.useMemo(
    () =>
      Array.from(new Set(products.map((item) => productCategory(item))))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "es")),
    [products],
  );

  const filteredProducts = React.useMemo(() => {
    const query = normalizeSearchValue(searchTerm);

    return products.filter((product) => {
      const itemType = normalizeItemKind(product);
      const isActive = productIsActive(product);
      const category = productCategory(product);
      const tags = tagsToText(product);
      const variantSearch = (product.variants || product.variations || [])
        .flatMap((variant) => [
          variant?.sku,
          variant?.barcode,
          ...Object.values(variant?.attributes || {}),
        ])
        .filter(Boolean)
        .join(" ");
      const searchableText = normalizeSearchValue(
        [
          productName(product),
          productDescription(product),
          category,
          productCode(product),
          tags,
          variantSearch,
        ].join(" "),
      );

      const matchesSearch = !query || searchableText.includes(query);
      const matchesType = typeFilter === "all" || itemType === typeFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? isActive : !isActive);
      const matchesCategory =
        categoryFilter === "all" || category === categoryFilter;

      return matchesSearch && matchesType && matchesStatus && matchesCategory;
    });
  }, [products, searchTerm, typeFilter, statusFilter, categoryFilter]);

  const paginatedProducts = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  const inventorySummary = React.useMemo(() => {
    const active = products.filter(productIsActive).length;
    const services = products.filter(
      (product) => normalizeItemKind(product) === "service",
    ).length;
    const internal = products.filter((product) =>
      ["supply", "expense", "asset"].includes(normalizeItemKind(product)),
    ).length;
    const lowStock = products.filter((product) => {
      const capabilities = productCapabilities(product);
      const threshold = Number(product.inventory?.reorderPoint ?? 5);
      return capabilities.stockTracked && productStock(product) <= threshold;
    }).length;

    return {
      total: products.length,
      active,
      services,
      internal,
      lowStock,
    };
  }, [products]);

  const hasActiveFilters = Boolean(
    searchTerm ||
    typeFilter !== "all" ||
    statusFilter !== "all" ||
    categoryFilter !== "all",
  );

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!mounted) return;
        setMemberAccess(normalizeProjectAccess(data.memberAccess || data.member || {}));
        if (data.project) setActiveProject(data.project);
      })
      .catch(() => {
        if (mounted) setMemberAccess(normalizeProjectAccess());
      });
    return () => {
      mounted = false;
    };
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    if (projectId && (!activeProject || activeProject.slug !== projectId)) {
      const found = projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      );
      if (found) setActiveProject(found);
    }
  }, [projectId, projects, activeProject, setActiveProject]);

  React.useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  React.useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const loadProducts = React.useCallback(async () => {
    if (!projectId || !project || !productsEnabled) {
      setLoading(false);
      setProductsLoaded(true);
      return;
    }
    try {
      setProductsLoaded(false);
      setLoading(true);
      setError("");
      const data = await productsApi.list(projectId);
      const nextProducts = Array.isArray(data?.products) ? data.products : [];
      setProducts(nextProducts);
      setTrialCapacity(data?.trial || null);
      window.dispatchEvent(
        new CustomEvent(TRIAL_PRODUCTS_EVENT, {
          detail: { projectId, count: nextProducts.length },
        }),
      );
    } catch (err) {
      setProducts([]);
      setTrialCapacity(null);
      setError(err.message || "No se pudieron cargar las publicaciones");
    } finally {
      setLoading(false);
      setProductsLoaded(true);
    }
  }, [projectId, project, productsEnabled]);

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  React.useEffect(() => {
    setPage(0);
  }, [searchTerm, typeFilter, statusFilter, categoryFilter, rowsPerPage]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredProducts.length / rowsPerPage) - 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [filteredProducts.length, page, rowsPerPage]);

  const resetUploads = React.useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setSelectedFiles([]);
    setPreviewUrls([]);
    setExistingImages([]);
    setRemovedImageIds([]);
    setImagePreparationProgress(null);
    setImageUploadProgress(null);
    setImageError("");
  }, []);

  const handleClose = () => {
    if (saving || processingImages) return;
    setOpen(false);
    resetUploads();
  };

  const requestUpgrade = React.useCallback(() => {
    setUpgradeOpen(true);
  }, []);

  const openCreateWithType = React.useCallback(
    (itemKind = "product") => {
      if (!canCreateItems) {
        setError("Tu rol no tiene permiso para crear ítems.");
        return;
      }
      if (trialLimitReached) {
        requestUpgrade();
        return;
      }
      const normalizedKind = ITEM_KIND_LABELS[itemKind] ? itemKind : "product";
      const capabilities = capabilitiesForKind(normalizedKind);
      setEditingProduct(null);
      setForm({
        ...emptyForm,
        erpItemKind: normalizedKind,
        itemType: normalizedKind === "service" ? "service" : "product",
        ...capabilities,
        requiresBooking: Boolean(capabilities.reservable),
        requiresShipping: Boolean(capabilities.shippable),
        attributes: [],
      });
      resetUploads();
      setError("");
      setOpen(true);
    },
    [canCreateItems, requestUpgrade, resetUploads, trialLimitReached],
  );

  const handleOpenCreate = React.useCallback(() => {
    openCreateWithType("product");
  }, [openCreateWithType]);

  React.useEffect(() => {
    const shouldCreate = searchParams.get("crear") === "1";
    if (!shouldCreate || !project || !productsEnabled || loading || !productsLoaded) return;

    const requestKey = `${projectId}:${searchParams.toString()}`;
    if (autoCreateHandledRef.current === requestKey) return;
    autoCreateHandledRef.current = requestKey;

    const requestedKind = ITEM_KIND_LABELS[searchParams.get("tipo")]
      ? searchParams.get("tipo")
      : "product";
    openCreateWithType(requestedKind);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("crear");
    nextParams.delete("tipo");
    setSearchParams(nextParams, { replace: true });
  }, [
    loading,
    productsLoaded,
    openCreateWithType,
    productsEnabled,
    project,
    projectId,
    searchParams,
    setSearchParams,
  ]);

  const handleOpenEdit = (product) => {
    if (!canUpdateItems) {
      setError("Tu rol no tiene permiso para editar ítems.");
      return;
    }
    setEditingProduct(product);
    const itemType = normalizeItemType(product);
    const erpItemKind = normalizeItemKind(product);
    const capabilities = productCapabilities(product);
    setForm({
      itemType,
      erpItemKind,
      name: product.name || product.nombre || "",
      description: product.description || product.descripcion || "",
      category: product.category || product.categoria || "General",
      code: product.code || product.codigo || "",
      unit: product.unit || product.unidad || "",
      priceType: normalizePriceType(product),
      price: String(product.price ?? product.precio ?? 0),
      stock: String(product.stock ?? 0),
      durationMinutes: String(
        product.durationMinutes ?? product.duracionMinutos ?? "",
      ),
      modality: product.modality || product.modalidad || "",
      requiresBooking: Boolean(capabilities.reservable),
      requiresShipping: Boolean(capabilities.shippable),
      bookingConfirmationMode:
        product.bookingConfirmationMode ||
        (product.confirmacionTurno === "automatica" ? "automatic" : "manual"),
      bookingPaymentMode:
        product.bookingPaymentMode || product.pagoReserva || "none",
      bookingDepositType:
        product.bookingDepositType || product.tipoSena || "fixed",
      bookingDepositValue: String(
        product.bookingDepositValue ?? product.valorSena ?? 0,
      ),
      capacity: String(product.capacity ?? product.capacidad ?? 1),
      bufferBeforeMinutes: String(
        product.bufferBeforeMinutes ?? product.bufferAntesMinutos ?? 0,
      ),
      bufferAfterMinutes: String(
        product.bufferAfterMinutes ?? product.bufferDespuesMinutos ?? 0,
      ),
      serviceLocation:
        product.serviceLocation || product.ubicacionServicio || "",
      meetingUrl: product.meetingUrl || product.enlaceVideollamada || "",
      bookingInstructions:
        product.bookingInstructions || product.instruccionesReserva || "",
      cancellationWindowHours: String(
        product.cancellationWindowHours ?? product.cancelacionHoras ?? 0,
      ),
      tags: tagsToText(product),
      attributes: attributesToRows(product.attributes),
      hasVariants: Boolean(
        product.hasVariants ??
          ((product.variantAttributes || product.variantOptions || []).length &&
            (product.variants || product.variations || []).length),
      ),
      variantAttributes: variantAttributesToForm(product),
      variants: variantsToForm(product),
      quantityPriceTiers: quantityPriceTiersToForm(product),
      active: Boolean(product.active ?? product.activo ?? true),
      ...capabilities,
      lastCost: String(product.costing?.lastCost ?? 0),
      averageCost: String(product.costing?.averageCost ?? 0),
      standardCost: String(product.costing?.standardCost ?? 0),
      costingMethod: product.costing?.costingMethod || "last_cost",
      costCurrency: product.costing?.currency || "ARS",
      allowNegativeStock: product.inventory?.allowNegativeStock === true,
      minimumStock: String(product.inventory?.minimumStock ?? 0),
      reorderPoint: String(product.inventory?.reorderPoint ?? 0),
      inventoryLocation: product.inventory?.location || "",
      salesAccountCode: product.accounting?.salesAccountCode || "SALES",
      purchaseAccountCode: product.accounting?.purchaseAccountCode || "PURCHASES",
      inventoryAccountCode: product.accounting?.inventoryAccountCode || "INVENTORY",
      costAccountCode: product.accounting?.costAccountCode || "COST_OF_GOODS",
      expenseAccountCode: product.accounting?.expenseAccountCode || "OTHER_EXPENSE",
      taxProfileCode: product.tax?.profileCode || "",
      taxRate: String(product.tax?.rate ?? 0),
      taxIncludedInPrice: product.tax?.includedInPrice === true,
    });
    resetUploads();

    const resolvedUrls = getProductImageUrls(product);
    const images = (product.images || [])
      .map((img, index) => ({
        identifier:
          (typeof img === "string"
            ? img
            : img?.path || img?.filename || img?.url) || "",
        url:
          resolvedUrls[index] ||
          (typeof img === "string" ? img : img?.url || img?.path) ||
          "",
      }))
      .filter((img) => img.identifier && img.url);
    setExistingImages(images.slice(0, MAX_IMAGES));
    setOpen(true);
  };

  const handleRemoveExistingImage = (identifier) => {
    setExistingImages((prev) =>
      prev.filter((img) => img.identifier !== identifier),
    );
    setRemovedImageIds((prev) =>
      prev.includes(identifier) ? prev : [...prev, identifier],
    );
    setImageError("");
  };

  const handleFilesChange = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;

    const available = Math.max(
      0,
      MAX_IMAGES - existingImages.length - selectedFiles.length,
    );
    if (!available) {
      setImageError(`La publicación ya tiene ${MAX_IMAGES} imágenes.`);
      return;
    }

    const accepted = picked.slice(0, available);
    if (picked.length > available) {
      setImageError(
        `Se tomarán ${available} archivo${available === 1 ? "" : "s"}. El máximo total es ${MAX_IMAGES}.`,
      );
    } else {
      setImageError("");
    }

    try {
      setProcessingImages(true);
      setImagePreparationProgress(0);
      const optimized = await optimizeProductImages(accepted, {
        onProgress: ({ percent }) => setImagePreparationProgress(percent),
      });
      const urls = optimized.map((file) => URL.createObjectURL(file));
      setSelectedFiles((prev) => [...prev, ...optimized]);
      setPreviewUrls((prev) => [...prev, ...urls]);
    } catch (err) {
      setImageError(err.message || "No se pudieron preparar las imágenes.");
    } finally {
      setProcessingImages(false);
      setImagePreparationProgress(null);
    }
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
    setPreviewUrls((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
    setImageError("");
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAttributeChange = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      attributes: prev.attributes.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const addAttribute = () => {
    setForm((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { label: "", value: "" }].slice(0, 30),
    }));
  };

  const removeAttribute = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: prev.attributes.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (processingImages) return;

    const editingId = productId(editingProduct);
    const creating = !editingId;
    if ((creating && !canCreateItems) || (!creating && !canUpdateItems)) {
      setError("Tu rol no tiene permiso para guardar este ítem.");
      return;
    }
    if (creating && trialLimitReached) {
      setOpen(false);
      resetUploads();
      requestUpgrade();
      return;
    }

    if (isReservableService(form) && Number(form.durationMinutes || 0) <= 0) {
      setError("Para ofrecer turnos, indicá cuánto dura el servicio.");
      return;
    }

    const variantError = validateVariantForm(form, {
      stockManagedBySystem: systemEnabled,
    });
    if (variantError) {
      setError(variantError);
      return;
    }

    const quantityDiscountError = validateQuantityDiscountRows(form);
    if (quantityDiscountError) {
      setError(quantityDiscountError);
      return;
    }

    try {
      setSaving(true);
      setError("");
      const payload = normalizeProductPayload(
        form,
        variantImageOptions(existingImages, previewUrls),
        {
          stockManagedBySystem: systemEnabled,
          creating,
        },
      );
      let savedProduct = null;
      const uploadOptions = selectedFiles.length
        ? {
            onUploadProgress: (progressEvent) => {
              const percent = uploadProgressPercent(progressEvent);
              if (percent !== null) setImageUploadProgress(percent);
            },
          }
        : {};
      setImageUploadProgress(selectedFiles.length ? 0 : null);
      if (editingId) {
        if (removedImageIds.length) payload.removeImages = removedImageIds;
        const response = await productsApi.update(
          projectId,
          editingId,
          payload,
          selectedFiles,
          uploadOptions,
        );
        savedProduct = response?.product || null;
      } else {
        const response = await productsApi.create(
          projectId,
          payload,
          selectedFiles,
          uploadOptions,
        );
        savedProduct = response?.product || null;
      }
      setOpen(false);
      resetUploads();
      await loadProducts();

      if (creating && isReservableService(form)) {
        const savedId = productId(savedProduct);
        navigate(
          `/dashboard/proyectos/${projectId}/agenda${savedId ? `?servicio=${savedId}&nuevo=1` : ""}`,
        );
      }
    } catch (err) {
      setError(
        selectedFiles.length
          ? describeImageUploadError(err, {
              action: "subir las imágenes del ítem",
              maxBytes: PRODUCT_IMAGE_MAX_SOURCE_BYTES,
            })
          : err.message || "No se pudo guardar la publicación",
      );
    } finally {
      setSaving(false);
      setImageUploadProgress(null);
    }
  };

  const handleDelete = async (product) => {
    if (!canDeleteItems) {
      setError("Tu rol no tiene permiso para eliminar ítems.");
      return;
    }
    const label =
      normalizeItemType(product) === "service" ? "servicio" : "producto";
    const ok = window.confirm(`¿Borrar el ${label} "${productName(product)}"?`);
    if (!ok) return;
    try {
      setError("");
      await productsApi.remove(projectId, productId(product));
      await loadProducts();
    } catch (err) {
      setError(err.message || "No se pudo borrar la publicación");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setTypeFilter("all");
    setStatusFilter("all");
    setCategoryFilter("all");
  };

  if (project && !productsEnabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Ítems del negocio"
        description="Este módulo unifica catálogo, servicios, insumos, gastos, stock y conceptos internos."
      />
    );
  }

  return (
    <Stack spacing={3} sx={{ pb: 4 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: -0.8 }}
          >
            Ítems del negocio
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.5 }}>
            {project?.name || projectId} · una sola base para catálogo, ERP, compras, gastos y stock
          </Typography>
        </Box>
        {canCreateItems && !trialNeedsFirstPublication ? (
          <Button
            startIcon={trialLimitReached ? <CreditCardRoundedIcon /> : <AddRoundedIcon />}
            variant="contained"
            disabled={loading || !productsLoaded}
            onClick={handleOpenCreate}
            sx={{
              bgcolor: BRAND.orange,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 900,
              px: 2.5,
              py: 1.15,
              boxShadow: "0 10px 24px rgba(243,90,58,0.2)",
              alignSelf: { xs: "stretch", md: "auto" },
              ...(trialLimitReached
                ? {
                    "@keyframes trialUpgradeGlow": {
                      "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.18)" },
                      "50%": { boxShadow: "0 0 0 9px rgba(243,90,58,0)" },
                    },
                    animation: "trialUpgradeGlow 2s ease-in-out infinite",
                  }
                : {}),
              "&:hover": { bgcolor: "#DB4B2F" },
            }}
          >
            {trialLimitReached ? "Agregar más con un plan" : "Nuevo ítem"}
          </Button>
        ) : null}
      </Stack>

      {error && !open ? <Alert severity="error">{error}</Alert> : null}

      {canCreateItems && trialNeedsFirstPublication && !loading ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3.5,
            border: "1px solid rgba(243,90,58,.24)",
            bgcolor: "rgba(243,90,58,.045)",
            px: { xs: 1.8, md: 2.2 },
            py: 1.6,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  flex: "0 0 auto",
                  borderRadius: 2.3,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(243,90,58,.12)",
                  color: BRAND.orange,
                }}
              >
                <AutoAwesomeRoundedIcon />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 950, letterSpacing: .55 }}>
                  MISIÓN DEL TRIAL
                </Typography>
                <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.2 }}>
                  Cargá tu primera publicación gratis
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: .25, color: "rgba(25,36,47,.58)" }}>
                  Elegí producto o servicio. Durante la prueba podés cargar hasta {trialItemsLimit} publicaciones.
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="contained"
                startIcon={<Inventory2RoundedIcon />}
                onClick={() => openCreateWithType("product")}
                sx={{
                  bgcolor: BRAND.orange,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 950,
                  px: 2.2,
                  "@keyframes firstPublicationGlow": {
                    "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.22)" },
                    "50%": { boxShadow: "0 0 0 9px rgba(243,90,58,0)" },
                  },
                  animation: "firstPublicationGlow 1.8s ease-in-out infinite",
                  "&:hover": { bgcolor: "#DB4B2F" },
                }}
              >
                Cargar producto
              </Button>
              <Button
                variant="outlined"
                startIcon={<DesignServicesRoundedIcon />}
                onClick={() => openCreateWithType("service")}
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.dark, borderColor: "rgba(25,36,47,.22)" }}
              >
                Cargar servicio
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      {isTrial && productsLoaded && !loading && products.length >= 1 ? (
        <Alert
          severity={trialLimitReached ? "warning" : "success"}
          icon={<RocketLaunchRoundedIcon fontSize="inherit" />}
          sx={{
            borderRadius: 3,
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 900 }}>
            Trial: {trialItemsUsed}/{trialItemsLimit} publicaciones cargadas. {trialLimitReached
              ? "Alcanzaste el máximo; podés editar o borrar una para liberar lugar."
              : `Todavía podés cargar ${trialItemsRemaining} más.`}
          </Typography>
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {[
          { label: "Ítems totales", value: inventorySummary.total },
          { label: "Activos", value: inventorySummary.active },
          { label: "Uso interno", value: inventorySummary.internal },
          { label: "Stock bajo", value: inventorySummary.lowStock },
        ].map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: "1px solid rgba(25,36,47,0.08)",
              bgcolor: "#fff",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "rgba(25,36,47,0.58)", fontWeight: 750 }}
            >
              {item.label}
            </Typography>
            <Typography
              variant="h4"
              sx={{ color: BRAND.dark, fontWeight: 950, mt: 0.5 }}
            >
              {item.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(25,36,47,0.09)",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0 18px 50px rgba(25,36,47,0.06)",
        }}
      >
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={1.5}
          alignItems={{ xl: "center" }}
          justifyContent="space-between"
          sx={{ p: { xs: 2, md: 2.5 } }}
        >
          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar por nombre, SKU, categoría o etiqueta..."
            size="small"
            sx={{
              width: { xs: "100%", xl: 420 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                bgcolor: "rgba(25,36,47,0.025)",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: "rgba(25,36,47,0.45)" }} />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", xl: "auto" } }}
          >
            <TextField
              select
              size="small"
              label="Tipo"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              sx={{ minWidth: { sm: 145 } }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="product">Productos</MenuItem>
              <MenuItem value="service">Servicios</MenuItem>
              <MenuItem value="supply">Insumos</MenuItem>
              <MenuItem value="expense">Gastos</MenuItem>
              <MenuItem value="asset">Activos</MenuItem>
              <MenuItem value="charge">Cargos</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Estado"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              sx={{ minWidth: { sm: 140 } }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Activos</MenuItem>
              <MenuItem value="inactive">Ocultos</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Categoría"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              sx={{ minWidth: { sm: 175 } }}
            >
              <MenuItem value="all">Todas</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>

            {hasActiveFilters ? (
              <Tooltip title="Limpiar búsqueda y filtros">
                <Button
                  onClick={clearFilters}
                  startIcon={<FilterAltOffRoundedIcon />}
                  sx={{
                    minWidth: { sm: 130 },
                    textTransform: "none",
                    fontWeight: 850,
                    color: BRAND.dark,
                  }}
                >
                  Limpiar
                </Button>
              </Tooltip>
            ) : null}

            <Tooltip title="Actualizar listado">
              <span>
                <IconButton
                  onClick={loadProducts}
                  disabled={loading}
                  aria-label="Actualizar productos"
                  sx={{
                    border: "1px solid rgba(25,36,47,0.12)",
                    borderRadius: 2.5,
                    color: BRAND.dark,
                    width: 40,
                    height: 40,
                  }}
                >
                  <RefreshRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider />
        {loading ? <LinearProgress /> : null}

        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 1.5,
            bgcolor: "rgba(25,36,47,0.018)",
            borderBottom: "1px solid rgba(25,36,47,0.07)",
          }}
        >
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.6)" }}>
            {filteredProducts.length} resultado
            {filteredProducts.length === 1 ? "" : "s"}
            {hasActiveFilters ? ` de ${products.length}` : ""}
          </Typography>
        </Box>

        <TableContainer sx={{ minHeight: 360 }}>
          <Table
            stickyHeader
            sx={{ minWidth: 1120 }}
            aria-label="Tabla de inventario"
          >
            <TableHead>
              <TableRow>
                {[
                  "Publicación",
                  "Tipo",
                  "SKU / Código",
                  "Categoría",
                  "Precio",
                  "Stock / Duración",
                  "Estado",
                  "Acciones",
                ].map((label) => (
                  <TableCell
                    key={label}
                    align={label === "Acciones" ? "right" : "left"}
                    sx={{
                      bgcolor: "#FAFBFC",
                      color: "rgba(25,36,47,0.56)",
                      fontSize: 12,
                      fontWeight: 950,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      borderBottom: "1px solid rgba(25,36,47,0.08)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && paginatedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 9, borderBottom: 0 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 3,
                          bgcolor: "rgba(25,36,47,0.05)",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <Inventory2RoundedIcon
                          sx={{ fontSize: 34, color: "rgba(25,36,47,0.3)" }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 950, color: BRAND.dark }}
                      >
                        {products.length
                          ? "No encontramos coincidencias"
                          : isTrial
                            ? "Tu catálogo está listo para empezar"
                            : "Todavía no hay publicaciones"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(25,36,47,0.58)",
                          textAlign: "center",
                        }}
                      >
                        {products.length
                          ? "Probá cambiando la búsqueda o quitando algún filtro."
                          : isTrial
                            ? "Elegí qué vendés y te llevamos directo al formulario correcto."
                            : "Creá el primer producto o servicio para comenzar el inventario."}
                      </Typography>
                      {products.length ? (
                        <Button
                          onClick={clearFilters}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Limpiar filtros
                        </Button>
                      ) : isTrial && canCreateItems ? (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                          <Button
                            onClick={() => openCreateWithType("product")}
                            variant="contained"
                            startIcon={<Inventory2RoundedIcon />}
                            sx={{
                              bgcolor: BRAND.orange,
                              textTransform: "none",
                              fontWeight: 900,
                              borderRadius: 2.5,
                            }}
                          >
                            Cargar producto
                          </Button>
                          <Button
                            onClick={() => openCreateWithType("service")}
                            variant="outlined"
                            startIcon={<DesignServicesRoundedIcon />}
                            sx={{
                              textTransform: "none",
                              fontWeight: 900,
                              borderRadius: 2.5,
                            }}
                          >
                            Cargar servicio
                          </Button>
                        </Stack>
                      ) : canCreateItems ? (
                        <Button
                          onClick={handleOpenCreate}
                          variant="contained"
                          startIcon={<AddRoundedIcon />}
                          sx={{
                            mt: 1,
                            bgcolor: BRAND.orange,
                            textTransform: "none",
                            fontWeight: 900,
                            borderRadius: 2.5,
                          }}
                        >
                          Crear publicación
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : null}

              {paginatedProducts.map((product) => {
                const itemKind = normalizeItemKind(product);
                const isService = itemKind === "service";
                const capabilities = productCapabilities(product);
                const isActive = productIsActive(product);
                const imageUrl = getProductImageUrls(product)[0];
                const stock = productStock(product);
                const displayName = productName(product);
                const description = productDescription(product);
                const code = productCode(product);
                const unit = product.unit || product.unidad || "";

                return (
                  <TableRow
                    key={productId(product)}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "rgba(243,90,58,0.025)" },
                    }}
                  >
                    <TableCell sx={{ minWidth: 320, py: 1.6 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          src={imageUrl || undefined}
                          alt={displayName}
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            bgcolor: "rgba(25,36,47,0.06)",
                            border: "1px solid rgba(25,36,47,0.08)",
                            color: "rgba(25,36,47,0.32)",
                          }}
                        >
                          {isService ? (
                            <DesignServicesRoundedIcon />
                          ) : (
                            <ImageRoundedIcon />
                          )}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: BRAND.dark,
                              fontWeight: 900,
                              lineHeight: 1.25,
                            }}
                          >
                            {displayName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(25,36,47,0.55)",
                              mt: 0.45,
                              maxWidth: 360,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {description || "Sin descripción"}
                          </Typography>
                          {product.hasVariants || product.variants?.length ? (
                            <Chip
                              size="small"
                              label={`${(product.variants || product.variations || []).length} variantes`}
                              sx={{ mt: 0.8, height: 22, fontWeight: 850 }}
                            />
                          ) : null}
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={ITEM_KIND_LABELS[itemKind] || itemKind}
                        variant="outlined"
                        sx={{
                          fontWeight: 800,
                          borderColor: capabilities.publicVisible
                            ? "rgba(25,118,210,0.28)"
                            : "rgba(123,91,181,0.3)",
                          color: capabilities.publicVisible ? "#1565C0" : "#6B4CA5",
                          bgcolor: capabilities.publicVisible
                            ? "rgba(25,118,210,0.04)"
                            : "rgba(123,91,181,0.05)",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: code ? "monospace" : "inherit",
                          color: code ? BRAND.dark : "rgba(25,36,47,0.38)",
                          fontWeight: code ? 750 : 500,
                        }}
                      >
                        {code || "Sin código"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ color: BRAND.dark }}>
                        {productCategory(product)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>
                        {priceLabel(product)}
                      </Typography>
                      {unit ? (
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(25,36,47,0.48)" }}
                        >
                          por {unit}
                        </Typography>
                      ) : null}
                    </TableCell>

                    <TableCell>
                      {isService ? (
                        <Box>
                          <Typography variant="body2" sx={{ color: BRAND.dark, fontWeight: 850 }}>
                            {durationLabel(product.durationMinutes || product.duracionMinutos)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.48)" }}>
                            {product.modality || product.modalidad || "Modalidad no indicada"}
                          </Typography>
                        </Box>
                      ) : capabilities.stockTracked ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            sx={{
                              color: stock <= Number(product.inventory?.reorderPoint ?? 5) ? "#C4452D" : BRAND.dark,
                              fontWeight: 950,
                            }}
                          >
                            {stock}
                          </Typography>
                          {stock <= Number(product.inventory?.reorderPoint ?? 5) ? (
                            <Chip
                              size="small"
                              label={stock === 0 ? "Sin stock" : "Reponer"}
                              sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 850,
                                color: "#B23D28",
                                bgcolor: "rgba(243,90,58,0.09)",
                              }}
                            />
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.48)" }}>
                          Sin control de stock
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={isActive ? "Activo" : "Oculto"}
                        sx={{
                          fontWeight: 850,
                          color: isActive ? "#237A45" : "rgba(25,36,47,0.58)",
                          bgcolor: isActive
                            ? "rgba(46,160,83,0.1)"
                            : "rgba(25,36,47,0.07)",
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        {canUpdateItems ? (
                          <Tooltip title="Editar ítem">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(product)}
                              aria-label={`Editar ${displayName}`}
                              sx={{ color: BRAND.dark }}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {canDeleteItems ? (
                          <Tooltip title="Eliminar ítem">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(product)}
                              aria-label={`Eliminar ${displayName}`}
                              sx={{ color: BRAND.orange }}
                            >
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <TablePagination
          component="div"
          count={filteredProducts.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{
            color: "rgba(25,36,47,0.68)",
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                fontWeight: 700,
              },
          }}
        />
      </Paper>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        fullScreen={mobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            minHeight: mobile ? "100dvh" : "min(780px, calc(100dvh - 48px))",
            maxHeight: mobile ? "100dvh" : "calc(100dvh - 48px)",
            borderRadius: mobile ? 0 : 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            px: { xs: 2, sm: 3 },
            py: { xs: 1.8, sm: 2.3 },
            pr: 7,
            color: "#fff",
            background: "linear-gradient(135deg,#19242F 0%,#30495B 74%,#3B5A70 100%)",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              width: 145,
              height: 145,
              right: -55,
              bottom: -95,
              borderRadius: "50%",
              bgcolor: "rgba(243,90,58,.27)",
            },
          }}
        >
          <Typography variant="overline" sx={{ fontWeight: 950, letterSpacing: ".12em", color: "#FFAA96" }}>
            CATÁLOGO Y OPERACIÓN
          </Typography>
          <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950, lineHeight: 1.15 }}>
            {editingProduct
              ? `Editar ${form.erpItemKind === "service" ? "servicio" : "ítem"}`
              : `Nuevo ${form.erpItemKind === "service" ? "servicio" : "ítem"}`}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,.72)", mt: 0.5 }}>
            Definí qué es, cómo se usa y cómo se presenta. El stock del Sistema se registra después mediante movimientos.
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={saving || processingImages}
            aria-label="Cerrar formulario"
            sx={{ position: "absolute", right: 10, top: 10, color: "#fff", zIndex: 1 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <DialogContent
            dividers
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overscrollBehavior: "contain",
              p: mobile ? 1.35 : 3,
              bgcolor: "#F6F8FA",
              scrollbarWidth: "thin",
              scrollbarColor: `${BRAND.orange} rgba(25,36,47,.10)`,
              "&::-webkit-scrollbar": { width: 10 },
              "&::-webkit-scrollbar-track": { bgcolor: "rgba(25,36,47,.07)" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: BRAND.orange,
                borderRadius: 999,
                border: "2px solid #F6F8FA",
              },
            }}
          >
            <Stack spacing={2.4}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.25, sm: 1.6 },
                  borderRadius: 3,
                  border: "1px solid rgba(25,36,47,.09)",
                  bgcolor: "#fff",
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  {[
                    ["1", "Crear el ítem", "Nombre, tipo y precio"],
                    ["2", "Definir su uso", systemEnabled ? "Venta, compra y control" : "Catálogo y stock manual"],
                    ["3", systemEnabled ? "Registrar existencia" : "Publicar", systemEnabled ? "Compra o inventario inicial" : "Guardar y mostrar"],
                  ].map(([number, label, detail]) => (
                    <Box
                      key={number}
                      sx={{
                        flex: 1,
                        p: 1.05,
                        borderRadius: 2.2,
                        bgcolor: number === "1" ? "rgba(243,90,58,.08)" : "rgba(25,36,47,.035)",
                        border: number === "1" ? "1px solid rgba(243,90,58,.22)" : "1px solid rgba(25,36,47,.06)",
                      }}
                    >
                      <Stack direction="row" spacing={0.9} alignItems="center">
                        <Chip
                          size="small"
                          label={number}
                          sx={{ minWidth: 28, fontWeight: 950, bgcolor: number === "1" ? BRAND.orange : BRAND.dark, color: "#fff" }}
                        />
                        <Box>
                          <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: BRAND.dark }}>
                            {label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
                            {detail}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              {systemEnabled && !editingProduct ? (
                <Alert severity="info" icon={<Inventory2RoundedIcon />}>
                  Guardar este ítem no crea stock ficticio. Se inicia en cero y después podés registrar una compra o el inventario inicial desde Sistema → Movimientos.
                </Alert>
              ) : null}

              <ItemFormHeading
                step="1"
                title="Información básica"
                description="Elegí la plantilla y cargá los datos que identifican al ítem para tu equipo y para el cliente."
              />

              <TextField
                select
                label="Plantilla de ítem"
                name="erpItemKind"
                value={form.erpItemKind}
                onChange={(event) => {
                  const nextKind = event.target.value;
                  setForm((current) => {
                    const nextCapabilities = capabilitiesForKind(nextKind);
                    const supportsVariants = supportsVariantsForKind(nextKind);
                    const isService = nextKind === "service";

                    return {
                      ...current,
                      erpItemKind: nextKind,
                      itemType: isService ? "service" : "product",
                      ...nextCapabilities,
                      requiresBooking: Boolean(nextCapabilities.reservable),
                      requiresShipping: Boolean(nextCapabilities.shippable),
                      stock: nextCapabilities.stockTracked ? current.stock : "0",
                      durationMinutes: isService ? current.durationMinutes : "",
                      modality: isService ? current.modality : "",
                      bookingConfirmationMode: isService
                        ? current.bookingConfirmationMode
                        : "manual",
                      bookingPaymentMode: isService
                        ? current.bookingPaymentMode
                        : "none",
                      bookingDepositType: isService
                        ? current.bookingDepositType
                        : "fixed",
                      bookingDepositValue: isService
                        ? current.bookingDepositValue
                        : "0",
                      capacity: isService ? current.capacity : "1",
                      bufferBeforeMinutes: isService
                        ? current.bufferBeforeMinutes
                        : "0",
                      bufferAfterMinutes: isService
                        ? current.bufferAfterMinutes
                        : "0",
                      serviceLocation: isService ? current.serviceLocation : "",
                      meetingUrl: isService ? current.meetingUrl : "",
                      bookingInstructions: isService
                        ? current.bookingInstructions
                        : "",
                      cancellationWindowHours: isService
                        ? current.cancellationWindowHours
                        : "0",
                      hasVariants: supportsVariants ? current.hasVariants : false,
                      variantAttributes: supportsVariants
                        ? current.variantAttributes
                        : [],
                      variants: supportsVariants ? current.variants : [],
                    };
                  });
                }}
                helperText="La plantilla propone capacidades iniciales, pero podés personalizarlas debajo."
                fullWidth
              >
                {Object.entries(ITEM_KIND_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </TextField>

              <TextField
                label={
                  form.erpItemKind === "service"
                    ? "Nombre del servicio"
                    : form.erpItemKind === "expense"
                      ? "Nombre del gasto"
                      : "Nombre del ítem"
                }
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
              />
              <TextField
                label="Descripción"
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                minRows={4}
                fullWidth
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Categoría"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  required
                  fullWidth
                />
                <TextField
                  label="Código interno / SKU"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  label="Unidad"
                  name="unit"
                  value={form.unit}
                  onChange={handleChange}
                  placeholder={
                    form.erpItemKind === "service"
                      ? "sesión, hora, visita"
                      : "unidad, kg, m², mes"
                  }
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="Forma de mostrar el precio"
                  name="priceType"
                  value={form.priceType}
                  onChange={handleChange}
                  fullWidth
                >
                  <MenuItem value="fixed">Precio fijo</MenuItem>
                  <MenuItem value="from">Desde</MenuItem>
                  <MenuItem value="quote">Consultar</MenuItem>
                </TextField>
                <TextField
                  label={
                    form.priceType === "quote"
                      ? "Precio de referencia (opcional)"
                      : "Precio"
                  }
                  name="price"
                  type="number"
                  value={form.price}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Stack>

              <QuantityDiscountsEditor form={form} setForm={setForm} />

              {form.erpItemKind === "service" || supportsVariantsForKind(form.erpItemKind) ? (
                <>
                  <Divider />
                  <ItemFormHeading
                    step="2"
                    title={form.erpItemKind === "service" ? "Cómo se presta el servicio" : "Variantes y presentación comercial"}
                    description={form.erpItemKind === "service"
                      ? "Completá duración, modalidad y turnos solamente cuando correspondan."
                      : "Configurá variantes sólo si el mismo ítem cambia por talle, color, modelo u otra opción."}
                  />

              {form.erpItemKind === "service" ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(25,36,47,0.02)" }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Datos del servicio
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completá duración y modalidad. Al activar turnos, el cliente verá “Ver horarios y reservar” en la web.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Duración estimada (minutos)"
                        name="durationMinutes"
                        type="number"
                        value={form.durationMinutes}
                        onChange={handleChange}
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                      <TextField
                        label="Modalidad"
                        name="modality"
                        value={form.modality}
                        onChange={handleChange}
                        placeholder="Presencial, online, a domicilio..."
                        fullWidth
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                          Requiere reserva o turno
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Publica la reserva online y, al guardar, te lleva a definir los horarios.
                        </Typography>
                      </Box>
                      <Switch
                        name="requiresBooking"
                        checked={isReservableService(form)}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          setForm((current) => ({
                            ...current,
                            reservable: enabled,
                            requiresBooking: enabled,
                            ...(!enabled
                              ? {
                                  bookingConfirmationMode: "manual",
                                  bookingPaymentMode: "none",
                                  bookingDepositType: "fixed",
                                  bookingDepositValue: "0",
                                  capacity: "1",
                                  bufferBeforeMinutes: "0",
                                  bufferAfterMinutes: "0",
                                  meetingUrl: "",
                                  bookingInstructions: "",
                                  cancellationWindowHours: "0",
                                }
                              : {}),
                          }));
                        }}
                      />
                    </Stack>

                    {isReservableService(form) ? (
                      <>
                        <Divider />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            select
                            label="Confirmación del turno"
                            name="bookingConfirmationMode"
                            value={form.bookingConfirmationMode}
                            onChange={handleChange}
                            fullWidth
                          >
                            <MenuItem value="manual">Confirmación manual</MenuItem>
                            <MenuItem value="automatic">Confirmación automática</MenuItem>
                          </TextField>
                          <TextField
                            select
                            label="Pago para reservar"
                            name="bookingPaymentMode"
                            value={form.bookingPaymentMode}
                            onChange={handleChange}
                            fullWidth
                          >
                            <MenuItem value="none">No requiere pago</MenuItem>
                            <MenuItem value="full">Pago total</MenuItem>
                            <MenuItem value="deposit">Seña</MenuItem>
                          </TextField>
                          <TextField
                            label="Cupos por horario"
                            name="capacity"
                            type="number"
                            value={form.capacity}
                            onChange={handleChange}
                            inputProps={{ min: 1 }}
                            fullWidth
                          />
                        </Stack>

                        {form.bookingPaymentMode === "deposit" ? (
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                              select
                              label="Tipo de seña"
                              name="bookingDepositType"
                              value={form.bookingDepositType}
                              onChange={handleChange}
                              fullWidth
                            >
                              <MenuItem value="fixed">Monto fijo</MenuItem>
                              <MenuItem value="percentage">Porcentaje</MenuItem>
                            </TextField>
                            <TextField
                              label={
                                form.bookingDepositType === "percentage"
                                  ? "Porcentaje de seña"
                                  : "Monto de seña"
                              }
                              name="bookingDepositValue"
                              type="number"
                              value={form.bookingDepositValue}
                              onChange={handleChange}
                              inputProps={{
                                min: 0,
                                max:
                                  form.bookingDepositType === "percentage"
                                    ? 100
                                    : undefined,
                              }}
                              fullWidth
                            />
                          </Stack>
                        ) : null}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            label="Margen antes (minutos)"
                            name="bufferBeforeMinutes"
                            type="number"
                            value={form.bufferBeforeMinutes}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                          <TextField
                            label="Margen después (minutos)"
                            name="bufferAfterMinutes"
                            type="number"
                            value={form.bufferAfterMinutes}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                          <TextField
                            label="Cancelación mínima (horas)"
                            name="cancellationWindowHours"
                            type="number"
                            value={form.cancellationWindowHours}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Stack>

                        <TextField
                          label="Lugar o dirección"
                          name="serviceLocation"
                          value={form.serviceLocation}
                          onChange={handleChange}
                          placeholder="Consultorio, local, domicilio del cliente..."
                          fullWidth
                        />
                        <TextField
                          label="Enlace de videollamada"
                          name="meetingUrl"
                          value={form.meetingUrl}
                          onChange={handleChange}
                          placeholder="https://meet.google.com/..."
                          fullWidth
                        />
                        <TextField
                          label="Instrucciones para reservar"
                          name="bookingInstructions"
                          value={form.bookingInstructions}
                          onChange={handleChange}
                          multiline
                          minRows={2}
                          placeholder="Qué debe traer, cómo prepararse, condiciones del turno..."
                          fullWidth
                        />
                      </>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}

                  {supportsVariantsForKind(form.erpItemKind) ? (
                    <ProductVariantsEditor
                      form={form}
                      setForm={setForm}
                      imageOptions={variantImageOptions(existingImages, previewUrls)}
                      stockManagedBySystem={systemEnabled}
                    />
                  ) : null}
                </>
              ) : null}

              <Divider />
              <ItemFormHeading
                step={form.erpItemKind === "service" || supportsVariantsForKind(form.erpItemKind) ? "3" : "2"}
                title="Uso operativo, inventario y costos"
                description={systemEnabled
                  ? "Definí dónde participa. Las existencias se protegen y cambian sólo mediante movimientos confirmados."
                  : "Definí dónde participa. Sin el módulo Sistema, el stock sigue siendo manual y no genera movimientos."}
              />

              <ItemOperationalFields
                form={form}
                setForm={setForm}
                canManageCosts={canManageCosts}
                canManageAccounting={canManageAccounting}
                stockManagedBySystem={systemEnabled}
                isExisting={Boolean(editingProduct)}
                canRegisterPurchase={canRegisterPurchase}
                canAdjustInventory={canAdjustInventory}
                compact={mobile || !systemEnabled}
                onCreateMovement={(kind) => {
                  const itemId = productId(editingProduct);
                  if (!itemId) return;
                  setOpen(false);
                  navigate(
                    `/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1&tipo=${kind}&itemId=${itemId}`,
                  );
                }}
                onOpenMovements={() => {
                  setOpen(false);
                  navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos`);
                }}
              />

              <Divider />
              <ItemFormHeading
                step={form.erpItemKind === "service" || supportsVariantsForKind(form.erpItemKind) ? "4" : "3"}
                title="Detalles para publicar"
                description="Terminá con etiquetas, datos propios del rubro e imágenes. Nada de esto altera el control operativo."
              />

              <TextField
                label="Etiquetas"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="Ej.: premium, instalación, exterior"
                helperText="Separalas con comas. Sirven para ordenar y describir cualquier rubro."
                fullWidth
              />

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={1}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                        Datos adicionales
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Agregá campos propios del rubro: marca, material,
                        cobertura, zona, nivel, garantía, etc.
                      </Typography>
                    </Box>
                    <Button
                      type="button"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={addAttribute}
                      sx={{ textTransform: "none", fontWeight: 850 }}
                    >
                      Agregar dato
                    </Button>
                  </Stack>

                  {form.attributes.length ? <Divider /> : null}
                  {form.attributes.map((row, index) => (
                    <Stack
                      key={`attribute-${index}`}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                    >
                      <TextField
                        label="Campo"
                        value={row.label}
                        onChange={(event) =>
                          handleAttributeChange(
                            index,
                            "label",
                            event.target.value,
                          )
                        }
                        placeholder="Ej.: Cobertura"
                        fullWidth
                      />
                      <TextField
                        label="Valor"
                        value={row.value}
                        onChange={(event) =>
                          handleAttributeChange(
                            index,
                            "value",
                            event.target.value,
                          )
                        }
                        placeholder="Ej.: Todo San Rafael"
                        fullWidth
                      />
                      <IconButton
                        type="button"
                        onClick={() => removeAttribute(index)}
                        aria-label="Eliminar dato"
                        sx={{ color: BRAND.orange }}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                    Ítem activo
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(25,36,47,0.6)" }}
                  >
                    Si está inactivo no podrá utilizarse en nuevas operaciones. La visibilidad pública se configura por separado.
                  </Typography>
                </Box>
                <Switch
                  name="active"
                  checked={form.active}
                  onChange={handleChange}
                />
              </Stack>

              <Box>
                <Typography sx={{ fontWeight: 900, color: BRAND.dark, mb: 1 }}>
                  Imágenes
                </Typography>

                {existingImages.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                  >
                    {existingImages.map((img) => (
                      <Box
                        key={img.identifier}
                        sx={{ position: "relative", mb: 1 }}
                      >
                        <Box
                          component="img"
                          src={img.url}
                          alt="Imagen actual"
                          sx={{
                            width: 76,
                            height: 76,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid rgba(25,36,47,0.1)",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveExistingImage(img.identifier)
                          }
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "white",
                            boxShadow: 1,
                            width: 24,
                            height: 24,
                            "&:hover": { bgcolor: "white" },
                          }}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                {previewUrls.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                  >
                    {previewUrls.map((url, index) => (
                      <Box key={url} sx={{ position: "relative", mb: 1 }}>
                        <Box
                          component="img"
                          src={url}
                          alt={`Nueva imagen ${index + 1}`}
                          sx={{
                            width: 76,
                            height: 76,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: `1px solid ${BRAND.orange}`,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            maxWidth: 76,
                            textAlign: "center",
                          }}
                        >
                          {formatImageSize(selectedFiles[index]?.size)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveSelectedFile(index)}
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "white",
                            boxShadow: 1,
                            width: 24,
                            height: 24,
                            "&:hover": { bgcolor: "white" },
                          }}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                {processingImages ? (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress
                      variant={Number.isFinite(imagePreparationProgress) ? "determinate" : "indeterminate"}
                      value={Number.isFinite(imagePreparationProgress) ? imagePreparationProgress : undefined}
                      aria-label="Progreso de preparación de imágenes"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Optimizando imágenes antes de subir
                      {Number.isFinite(imagePreparationProgress) ? `… ${imagePreparationProgress}%` : "…"}
                    </Typography>
                  </Box>
                ) : null}

                {saving && selectedFiles.length ? (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress
                      variant={Number.isFinite(imageUploadProgress) ? "determinate" : "indeterminate"}
                      value={Number.isFinite(imageUploadProgress) ? imageUploadProgress : undefined}
                      aria-label="Progreso de carga de imágenes"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {imageUploadProgress === 100
                        ? "Imágenes enviadas. El servidor está terminando de procesarlas…"
                        : `Subiendo imágenes${Number.isFinite(imageUploadProgress) ? `… ${imageUploadProgress}%` : "…"}`}
                    </Typography>
                  </Box>
                ) : null}

                {imageError ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {imageError}
                  </Alert>
                ) : null}

                <Button
                  component="label"
                  disabled={
                    processingImages ||
                    existingImages.length + selectedFiles.length >= MAX_IMAGES
                  }
                  startIcon={<UploadRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 850,
                    color: BRAND.dark,
                    border: "1px dashed rgba(25,36,47,0.3)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                  }}
                >
                  {existingImages.length || selectedFiles.length
                    ? "Agregar más imágenes"
                    : "Subir imágenes"}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFilesChange}
                  />
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.7,
                    color: "rgba(25,36,47,0.55)",
                  }}
                >
                  {existingImages.length + selectedFiles.length}/{MAX_IMAGES}{" "}
                  imágenes. JPG, PNG o WebP de hasta {formatImageSize(PRODUCT_IMAGE_MAX_SOURCE_BYTES)} cada una.
                  El panel las reduce a WebP antes de enviarlas para acelerar la carga.
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              flex: "0 0 auto",
              p: mobile ? 1.2 : 2,
              gap: 1,
              bgcolor: "#fff",
              borderTop: "1px solid rgba(25,36,47,.10)",
              boxShadow: "0 -10px 28px rgba(25,36,47,.06)",
            }}
          >
            <Button
              onClick={handleClose}
              disabled={saving || processingImages}
              fullWidth={mobile}
              sx={{ textTransform: "none", fontWeight: 850 }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving || processingImages}
              variant="contained"
              fullWidth={mobile}
              sx={{
                bgcolor: BRAND.orange,
                borderRadius: 999,
                minHeight: 44,
                textTransform: "none",
                fontWeight: 950,
                "&:hover": { bgcolor: "#D9472B" },
              }}
            >
              {saving && selectedFiles.length
                ? imageUploadProgress === 100
                  ? "Procesando imágenes…"
                  : `Subiendo${Number.isFinite(imageUploadProgress) ? ` ${imageUploadProgress}%` : "…"}`
                : saving
                  ? "Guardando..."
                  : editingProduct
                    ? "Guardar cambios"
                    : "Crear ítem"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <Box
          sx={{
            p: { xs: 2.5, sm: 3 },
            color: "#fff",
            background: "linear-gradient(135deg,#19242F 0%,#30485B 100%)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 52,
                height: 52,
                flex: "0 0 auto",
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(243,90,58,.18)",
                color: "#FF9A82",
              }}
            >
              <LockClockRoundedIcon />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: "rgba(255,255,255,.62)", fontWeight: 950, letterSpacing: ".1em" }}>
                LÍMITE DEL TRIAL
              </Typography>
              <DialogTitle sx={{ p: 0, fontWeight: 950, fontSize: { xs: 24, sm: 30 }, lineHeight: 1.12 }}>
                La prueba incluye hasta {trialItemsLimit} publicaciones
              </DialogTitle>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ pt: 2.7 }}>
          <Typography sx={{ color: "rgba(25,36,47,.68)", lineHeight: 1.65 }}>
            Ya usaste las {trialItemsLimit} publicaciones incluidas en la prueba. Podés editar o borrar las existentes; para cargar una nueva, liberá un lugar o activá el plan del proyecto.
          </Typography>

          <Stack spacing={1.1} sx={{ mt: 2 }}>
            {[`Editar las ${trialItemsLimit} publicaciones existentes`, "Cargar productos y servicios adicionales", "Mantener las funciones comerciales habilitadas"].map((label, index) => (
              <Stack key={label} direction="row" spacing={1.1} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: index === 0 ? "rgba(46,125,50,.09)" : "rgba(243,90,58,.09)",
                    color: index === 0 ? "#2E7D32" : BRAND.orange,
                    fontWeight: 950,
                    fontSize: 13,
                  }}
                >
                  {index === 0 ? "✓" : index + 1}
                </Box>
                <Typography sx={{ fontWeight: 850, color: BRAND.dark }}>{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1, flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1 }}>
          <Button
            fullWidth
            onClick={() => {
              setUpgradeOpen(false);
              navigate(landingPath);
            }}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}
          >
            Completar mi landing
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<CreditCardRoundedIcon />}
            onClick={() => {
              setUpgradeOpen(false);
              navigate(billingPath);
            }}
            sx={{
              bgcolor: BRAND.orange,
              textTransform: "none",
              fontWeight: 950,
              borderRadius: 999,
              "&:hover": { bgcolor: "#DB4B2F" },
            }}
          >
            Ver planes y activar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
